import { useMemo } from 'react';
import { useWebSocketConversation } from '@features/Admin/Campaign/hooks/useWebSocketConversation';
import { CallActors } from '@shared/constants/recipient-status.constants';
import type { ValidatedConversationLogDto } from '@shared/utils/conversationLog.utils';
import type { TestingConversationViewMessage } from './useTestingConversationMessages';

const isSupervisorText = (
  message: TestingConversationViewMessage,
): message is Extract<TestingConversationViewMessage, { toolCall?: never }> =>
  message.actor === CallActors.SUPERVISOR && message.type !== 'tool_call';

export const toSupervisorMessages = (
  logs: ValidatedConversationLogDto[],
): TestingConversationViewMessage[] =>
  logs
    .filter((log) => log.actor === CallActors.SUPERVISOR && log.type !== 'tool_call')
    .map((log) => {
      const receivedTime = new Date(log.createdAt).getTime();

      return {
        id: log.id,
        actor: log.actor,
        message: (log.message ?? '').trim(),
        type: 'text' as const,
        createdAt: log.createdAt,
        updatedAt: log.updatedAt,
        firstReceivedTime: receivedTime,
        lastReceivedTime: receivedTime,
      };
    })
    .filter((message) => message.message.length > 0);

const isSameSupervisorLine = (liveText: string, historyText: string): boolean =>
  liveText.length > 0 && historyText.startsWith(liveText);

export const dropDuplicatedSupervisorMessages = (
  historyMessages: TestingConversationViewMessage[],
  liveMessages: TestingConversationViewMessage[],
): TestingConversationViewMessage[] => {
  const liveSupervisorLines = liveMessages.filter(isSupervisorText);
  if (liveSupervisorLines.length === 0) return historyMessages;

  return historyMessages.filter((message) => {
    if (!isSupervisorText(message)) return true;

    return !liveSupervisorLines.some((liveMessage) =>
      isSameSupervisorLine(liveMessage.message.trim(), message.message.trim()),
    );
  });
};

export const useSupervisorTranscriptions = (
  callId?: string | null,
): TestingConversationViewMessage[] => {
  const { conversationData } = useWebSocketConversation(callId ?? '');

  return useMemo(() => toSupervisorMessages(conversationData.data), [conversationData.data]);
};
